import { makeStyles } from '@mui/styles';
import Grid from 'apollo-react/components/Grid';
import Loader from 'apollo-react/components/Loader';
import Tag from 'apollo-react/components/Tag';
import Typography from 'apollo-react/components/Typography';
import qs from 'query-string';
import React, { useEffect, useMemo, useState } from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';

import DocumentGoToPage from './components/DocumentGoToPage';
import DocumentRepoSelect from './components/DocumentRepoSelect';
import DocumentSearchPage from './components/DocumentSearchPage';
import DocumentSearchTableWrapper from './components/DocumentSearchTableWrapper';
import DocumentTree from './components/DocumentTree';
import {
  clearDocumentRepositoryDetails,
  clearDocumentRepositoryStore,
  fetchDocumentRepositories,
  fetchDocumentRepositoryDetails,
} from '../../actions/DocumentRepositoryActions';
import { clearErrors } from '../../actions/ErrorActions';
import { setLastSelectedRepositoryName } from '../../actions/SavedSearchFiltersActions';
import Layout from '../../components/Layout';
import * as Urls from '../../constants/Urls';
import { useTranslation } from '../../custom-hooks/useTranslation';
import { equalityFn } from '../../form-designer/utils/form-manager.utils';
import {
  getLastSelectedRepositoryName,
  getRepositoryInfo,
} from '../../selectors/DocumentRepositorySelectors';
import { mapRepositoryDetails } from '../../utils/commonUtils';
import { isEmpty } from '../../utils/ramdaUtils';
import AttachmentPreviewDialog from '../EntityDisplay/AttachmentPreviewDialog';

const useStyles = makeStyles({
  container: {
    height: '100%',
  },
  containerTree: {
    height: '100%',
    backgroundColor: '#fff',
    borderRightStyle: 'solid',
    borderRightWidth: 1,
    borderRightColor: '#dfdfdf',
    padding: 24,
  },
});

const DocumentRepository = () => {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const translateString = useTranslation();

  const {
    documentRepositories,
    documentRepositoryDetails,
    loading,
    defaultRepository,
    isDefaultSetForUser,
  } = useSelector(getRepositoryInfo);

  const lastSelectedRepositoryName = useSelector(getLastSelectedRepositoryName);
  const lastSelectedSearchNames = useSelector(
    (state) => state.savedSearchFilters.lastSelectedSearchNames ?? {},
    equalityFn
  );

  const [repositoryName, setRepositoryName] = useState('');
  const [selectedRepositoryItem, setSelectedRepositoryItem] = useState({});
  const [selectedDocuments, setSelectedDocuments] = useState([]);

  const [lastRepositoryItem, setLastRepositoryItem] = useState({});

  const qsParams = qs.parse(location.search) || {};

  useEffect(() => {
    document.title = translateString('Document Repository');
    dispatch(fetchDocumentRepositories());

    return () => {
      sessionStorage.setItem('backButtonURL', location.pathname + location.search);
      dispatch(clearErrors());
      dispatch(clearDocumentRepositoryStore());
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (repositoryName) {
      dispatch(fetchDocumentRepositoryDetails({ repositoryName }));
      setLastRepositoryItem(lastSelectedSearchNames[repositoryName] ?? {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [repositoryName, dispatch]);

  const repositoryItems = useMemo(() => {
    const nodes = documentRepositoryDetails.ChildNodes || [];
    return nodes.reduce(mapRepositoryDetails, {});
  }, [documentRepositoryDetails]);

  const handleRepositoryName = (repoName) => {
    if (repoName !== '') {
      setSelectedRepositoryItem({});
      setRepositoryName(repoName);
      setSelectedDocuments([]);

      batch(() => {
        dispatch(setLastSelectedRepositoryName(repoName));
        dispatch(clearDocumentRepositoryDetails());
      });
    }
  };

  const handleSelectedItem = (item) => {
    const { repositoryName } = item;
    if (repositoryName && qsParams.repositoryName !== repositoryName) {
      history.push(`${Urls.DOCUMENT_REPOSITORY}?${qs.stringify({ ...qsParams, repositoryName })}`);
    }
    setSelectedRepositoryItem(item);
    setSelectedDocuments([]);
  };

  const selectProps = {
    defaultRepositoryName: defaultRepository,
    lastSelectedRepositoryName,
    repositoryName,
    handleRepositoryName,
    documentRepositories: documentRepositories.map((repo) => ({
      translatedSearchName: translateString(repo.Name),
      id: repo.Id,
      searchName: repo.Name,
    })),
  };

  const treeProps = {
    onSelectedItem: handleSelectedItem,
    lastSelectedItem: lastRepositoryItem,
    repositoryName,
    documentRepositoryDetails,
    repositoryItems,
    defaultRepository,
    documentRepositories,
    isDefaultSetForUser,
  };

  return (
    <Layout>
      {loading && <Loader />}
      <Grid container className={classes.container}>
        <Grid item xs={12} md={3}>
          <Grid container className={classes.containerTree}>
            <Grid item xs={12}>
              <Typography variant="h2" gutterBottom>
                {translateString('Document Repository')}
              </Typography>
              {repositoryName && (
                <Tag
                  label={`${translateString(repositoryName)} ${
                    selectedRepositoryItem.NodeText
                      ? ` / ${translateString(selectedRepositoryItem.NodeText)}`
                      : ''
                  }`}
                  variant="fuchsia"
                />
              )}
              {documentRepositories?.length ? (
                <DocumentRepoSelect {...selectProps} />
              ) : (
                <Typography>{`${translateString('Loading')}...`}</Typography>
              )}
              {!isEmpty(documentRepositoryDetails) && <DocumentTree {...treeProps} />}
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12} md={9}>
          {selectedRepositoryItem.queryName && (
            <DocumentSearchTableWrapper
              selectedRepositoryItem={selectedRepositoryItem}
              selectedDocuments={selectedDocuments}
              setSelectedDocuments={setSelectedDocuments}
            />
          )}
          {selectedRepositoryItem.page === 'search' && <DocumentSearchPage />}
          {selectedRepositoryItem.page === 'goto' && <DocumentGoToPage />}
        </Grid>
      </Grid>
      <AttachmentPreviewDialog />
    </Layout>
  );
};

export default DocumentRepository;
